/**
 * Lab accession -> specimen workflow: patient verification, specimen creation/collection/receipt,
 * accept/reject + recollection, four-eyes verify & release, external-lab routing, traceability.
 * Backs /lab/accessions/... on the backend (LabAccessionController) — a separate prefix from the
 * older /inpatient/labs endpoints (order placement, result entry — see modules/inpatient.ts).
 */
import { fetchApi, fetchGetFileApi } from '../client';

export const labAccessionModule = {
  // sinceDays only applies when history=true — server defaults to 90 days, caps at 730.
  list: (history = false, sinceDays?: number) =>
    fetchApi<any[]>(`/api/lab/accessions?history=${history}${sinceDays ? `&sinceDays=${sinceDays}` : ''}`),

  get: (accessionId: string) =>
    fetchApi<any>(`/api/lab/accessions/${accessionId}`),

  /** Single-fetch payload for the routed lab pages (verify/collect/timeline/results): accession +
   *  patient identity + specimens + orders. */
  getDetail: (accessionId: string) =>
    fetchApi<any>(`/api/lab/accessions/${accessionId}/detail`),

  getSpecimens: (accessionId: string) =>
    fetchApi<any[]>(`/api/lab/accessions/${accessionId}/specimens`),

  /** Scan-to-receive lookup — the Laboratory Receiving screen doesn't know which accession a
   *  scanned barcode belongs to ahead of time. */
  findSpecimenByBarcode: (barcode: string) =>
    fetchApi<any>(`/api/lab/accessions/specimens/search?barcode=${encodeURIComponent(barcode)}`),

  getTimeline: (accessionId: string) =>
    fetchApi<any[]>(`/api/lab/accessions/${accessionId}/timeline`),

  /** One combined PDF of every completed test on this accession. */
  downloadReport: (accessionId: string) =>
    fetchGetFileApi(`/api/lab/accessions/${accessionId}/report`),

  verifyPatient: (accessionId: string, data: { method: string; priority?: string; collectionLocation?: string }) =>
    fetchApi<any>(`/api/lab/accessions/${accessionId}/verify-patient`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  createSpecimens: (accessionId: string) =>
    fetchApi<any[]>(`/api/lab/accessions/${accessionId}/specimens`, { method: 'POST' }),

  getSpecimenLabel: (accessionId: string, specimenId: string) =>
    fetchApi<any>(`/api/lab/accessions/${accessionId}/specimens/${specimenId}/label`),

  collectSpecimen: (accessionId: string, specimenId: string, collectionSite?: string) =>
    fetchApi<any>(`/api/lab/accessions/${accessionId}/specimens/${specimenId}/collect`, {
      method: 'POST',
      body: JSON.stringify({ collectionSite }),
    }),

  receiveSpecimen: (accessionId: string, specimenId: string) =>
    fetchApi<any>(`/api/lab/accessions/${accessionId}/specimens/${specimenId}/receive`, { method: 'POST' }),

  rejectSpecimen: (
    accessionId: string,
    specimenId: string,
    data: { category: string; reason: string; recollectionRequired: boolean }
  ) =>
    fetchApi<any>(`/api/lab/accessions/${accessionId}/specimens/${specimenId}/reject`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  verifySpecimen: (accessionId: string, specimenId: string) =>
    fetchApi<any>(`/api/lab/accessions/${accessionId}/specimens/${specimenId}/verify`, { method: 'POST' }),

  /** Correct an already-released result value. LAB_ADMIN/CLINIC_ADMIN only (server-enforced). */
  amendResult: (accessionId: string, specimenId: string, resultValueId: number | string, data: { newValue: string; reason: string }) =>
    fetchApi<any>(`/api/lab/accessions/${accessionId}/specimens/${specimenId}/results/${resultValueId}/amend`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getAmendmentHistory: (accessionId: string, specimenId: string, resultValueId: number | string) =>
    fetchApi<any[]>(`/api/lab/accessions/${accessionId}/specimens/${specimenId}/results/${resultValueId}/amendments`),

  sendToExternal: (accessionId: string, specimenId: string, data: { referenceNumber?: string; notes?: string }) =>
    fetchApi<any>(`/api/lab/accessions/${accessionId}/specimens/${specimenId}/send-external`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  receiveExternalResult: (accessionId: string, specimenId: string, notes?: string) =>
    fetchApi<any>(`/api/lab/accessions/${accessionId}/specimens/${specimenId}/receive-external-result`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    }),
};
